/**
 * @file Contract reference-for-tenant saga: writes `referenceForTenant` on its own
 * route, so the value can be changed on a contract the general `PUT contract/:id`
 * refuses. That route answers 403 `be_update_remove_signed_contract` for every
 * signed contract, which is the state almost all live contracts are in, and their
 * charges are exactly the ones whose PDF prints the reference.
 *
 * The route takes `{ referenceForTenant }` (string, 0..255 — an empty string is how
 * the field is cleared, the column is NOT NULL) and answers `{ data: <contract> }`
 * with the whole re-read contract, so the entity sits at `res.data.data`.
 *
 * Flow: `GLOBAL_CONTRACT_REFERENCE_FOR_TENANT_UPDATE_START` →
 * `PAGE_CONTRACT_UPDATE_ERRORS_CLEAR` →
 * `GLOBAL_CONTRACT_REFERENCE_FOR_TENANT_UPDATE_REQUEST` →
 * `Http.put("contract/:id/reference-for-tenant")` → unwraps `res.data.data` →
 * `ContractFactory` → `GLOBAL_CONTRACT_REFERENCE_FOR_TENANT_UPDATE_SUCCESS` plus
 * `PAGE_CONTRACT_UPDATE_SUCCESS` so the store's contract is refreshed →
 * `action.onSuccess(contract)`.
 * Exceptions: `be_ajv_not_validated` (400, over 255 characters) and
 * `be_contract_not_found` (404, also for a non-numeric id) — both translated
 * already. The catch runs `ErrorHandlerSequence`, which shows the snackbar and
 * calls `action.onError` to reset the caller's saving flag, then dispatches
 * `GLOBAL_CONTRACT_REFERENCE_FOR_TENANT_UPDATE_FAILURE`, whose reducer case writes
 * `err.fields` so the page's `getErrorFor("referenceForTenant")` lights the input.
 *
 * Known Issues: this is not the column's only writer — `referenceForTenant` is also
 * in the general contract update schema, so a contract-form save on a non-signed
 * contract carrying a stale value overwrites what this route wrote.
 */
import { call, put, takeEvery } from "redux-saga/effects";
import Constants from "../../Action/Constants";
import { ErrorHandlerSequence } from "../Sequences/ErrorHandlerSequence";
import Http from "../../Http/Http";
import ContractFactory from "../../Entity/Factories/ContractFactory";
import { ActionInterface } from "../../Interface/ActionInterface";
import { ContractInterface } from "../../Entity/Entities/Contract/ContractInterface";

export default function* contractChangeReferenceForTenant(action: ActionInterface): any {
  try {
    // Clears any field error a previous attempt left behind, the same way the general
    // contract update saga opens.
    yield put({
      type: Constants.PAGE_CONTRACT_UPDATE_ERRORS_CLEAR
    });

    yield put({
      type: Constants.GLOBAL_CONTRACT_REFERENCE_FOR_TENANT_UPDATE_REQUEST
    });

    const res = yield call(Http.put, `contract/${action.payload?.contractId}/reference-for-tenant`, {
      referenceForTenant: action.payload?.referenceForTenant
    });

    const contract: ContractInterface = ContractFactory.create(res.data.data);

    yield put({
      type: Constants.GLOBAL_CONTRACT_REFERENCE_FOR_TENANT_UPDATE_SUCCESS
    });

    // The store holds the contract the page re-seeds its inputs from, so the
    // updated row has to land there — otherwise the next `initializePage` puts
    // the pre-save value back into the field the user just wrote.
    yield put({
      type: Constants.PAGE_CONTRACT_UPDATE_SUCCESS,
      payload: {
        contract: contract
      }
    });

    if (action.hasOwnProperty("onSuccess") === true) {
      action.onSuccess.call(this, contract);
    }
  } catch (err) {
    yield ErrorHandlerSequence(err, action);

    yield put({
      type: Constants.GLOBAL_CONTRACT_REFERENCE_FOR_TENANT_UPDATE_FAILURE,
      payload: {
        err: err
      }
    });
  }
}

export function* watchContractChangeReferenceForTenant() {
  yield takeEvery(Constants.GLOBAL_CONTRACT_REFERENCE_FOR_TENANT_UPDATE_START, contractChangeReferenceForTenant);
}
